import React from 'react';
import { Compass, ArrowUpRight } from 'lucide-react';

/**
 * Viator affiliate CTA card (iter 77).
 *
 * Single source of truth for the partner-shop link. Embedded on:
 *   1. City Guide → after Quality-of-Life section (CityGuideMagazine.js)
 *   2. Blueprint success screen (PortalBlueprint.js)
 *   3. Events empty state (PortalEvents.js)
 *
 * Always opens in a new tab. Visible to ALL tiers, never paywalled.
 *
 * Iter 78 - fires anonymous click tracking to /api/affiliates/track. The
 * fetch uses `keepalive` so the request still flies even though the new
 * tab opens immediately. Failures are swallowed; the outbound click is
 * never blocked by tracking errors.
 */
export const VIATOR_URL =
  'https://www.viator.com/partner-shop/mundalo?medium=link&medium_version=shop&campaign=Mundalotours';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const trackAffiliateClick = ({ surface, cityName, userRole }) => {
  try {
    // Best-effort fire-and-forget; `keepalive` allows the request to
    // complete after the page begins unloading / new tab focus shift.
    fetch(`${API_URL}/api/affiliates/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        surface,
        city_name: cityName || null,
        user_role: userRole || null,
      }),
      keepalive: true,
    }).catch(() => { /* swallow - tracking must never block the click */ });
  } catch {
    /* noop */
  }
};

const ViatorCtaCard = ({
  headline,
  subtext,
  buttonLabel = 'Browse Experiences →',
  eyebrow = 'Experiences & Tours',
  testId = 'viator-cta-card',
  className = '',
  // Iter 78 - tracking metadata. Surface is required for the rollup;
  // cityName + userRole are optional context.
  surface,
  cityName,
  userRole,
}) => (
  <section
    className={`bg-white border-2 border-[#2CA8A0]/45 hover:border-[#2CA8A0] rounded-2xl p-6 sm:p-8 transition ${className}`}
    data-testid={testId}
  >
    <div className="flex flex-col sm:flex-row items-start gap-5">
      <div className="w-12 h-12 rounded-xl bg-[#2CA8A0]/12 flex items-center justify-center shrink-0">
        <Compass className="w-6 h-6 text-[#1A5C6E]" />
      </div>
      <div className="flex-1 min-w-0">
        {eyebrow && (
          <p
            className="font-mono uppercase text-[10px] tracking-[0.2em] text-[#1A5C6E] mb-2"
            data-testid={`${testId}-eyebrow`}
          >
            {eyebrow}
          </p>
        )}
        <h3
          className="font-display italic text-2xl md:text-3xl text-[#0A1F2E] leading-tight"
          data-testid={`${testId}-headline`}
        >
          {headline}
        </h3>
        {subtext && (
          <p
            className="text-[#0A1F2E]/75 mt-2 text-sm md:text-base leading-relaxed"
            data-testid={`${testId}-subtext`}
          >
            {subtext}
          </p>
        )}
        <a
          href={VIATOR_URL}
          target="_blank"
          rel="noopener noreferrer sponsored"
          onClick={() => surface && trackAffiliateClick({ surface, cityName, userRole })}
          className="mt-4 inline-flex items-center gap-2 bg-[#0A1F2E] hover:bg-[#163448] text-white font-mono uppercase text-[11px] tracking-[0.15em] px-5 py-2.5 rounded-full transition"
          data-testid={`${testId}-button`}
        >
          {buttonLabel}
          <ArrowUpRight className="w-3.5 h-3.5" />
        </a>
      </div>
    </div>
  </section>
);

export default ViatorCtaCard;
